import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Controller, useForm } from 'react-hook-form';
import { useAppSelector } from '../store/store';
import { selectIsAuthenticated, selectUser } from '../features/auth/authSlice';
import { api } from '../api/remembrApi';
import { analytics } from '../utils/analytics';
import { Input } from './Input';
import { PrimaryCTA } from './PrimaryCTA';
import { StarRating } from './StarRating';
import {
  ScreenshotCaptureError,
  captureScreenshot,
  isScreenshotSupported,
} from '../utils/screenshotCapture';
import {
  generateSessionId,
  getBrowserLanguage,
  getDeviceType,
  getOperatingSystem,
  getScreenResolution,
  getViewportSize,
} from '../utils/deviceDetection';
import { fpColors } from '../styles/theme';
import type {
  FeedbackFormData,
  FeedbackSubmissionRequest,
  FeedbackType,
} from '../api/types';

/**
 * Fidelity Purple feedback form — rewritten off legacy
 * colors/spacing/fontSizes/Ionicons + legacy Button. Every thunk,
 * form validation, screenshot capture, analytics call, and submission
 * path is preserved verbatim; only the visuals and the submit button
 * changed. Used by FeedbackScreen and FeedbackModal.
 */
interface FeedbackFormProps {
  onSubmitSuccess?: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  showScreenshot?: boolean;
}

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'bug_report', label: 'Bug report' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'general_feedback', label: 'General feedback' },
  { value: 'user_experience', label: 'User experience' },
  { value: 'performance_issue', label: 'Performance issue' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'compliment', label: 'Compliment' },
];

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  onSubmitSuccess,
  onCancel,
  showCancelButton = false,
  showScreenshot = true,
}) => {
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FeedbackFormData>({
    defaultValues: {
      feedback_type: 'general_feedback',
      feedback_title: '',
      feedback_content: '',
      user_email: '',
      user_name: '',
      rating: 0,
      would_recommend: undefined,
      follow_up_requested: false,
    },
  });

  const rating = watch('rating');

  const getContextData = (): Partial<FeedbackFormData> => ({
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    page_title: typeof document !== 'undefined' ? document.title : '',
    referrer_url: typeof document !== 'undefined' ? document.referrer : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    screen_resolution: getScreenResolution(),
    browser_language: getBrowserLanguage(),
    viewport_size: getViewportSize(),
    device_type: getDeviceType(),
    operating_system: getOperatingSystem(),
    session_id: generateSessionId(),
  });

  const handleCaptureScreenshot = async () => {
    if (!isScreenshotSupported()) {
      Alert.alert(
        'Screenshot not supported',
        'Your browser doesn\u2019t support screenshot capture.',
        [{ text: 'OK' }],
      );
      return;
    }
    setIsCapturing(true);
    try {
      const capturedFile = await captureScreenshot();
      setScreenshot(capturedFile);
    } catch (error) {
      if (error instanceof ScreenshotCaptureError) {
        Alert.alert('Screenshot failed', error.message, [{ text: 'OK' }]);
      } else {
        Alert.alert(
          'Screenshot failed',
          'Unable to capture screenshot. You can still submit without it.',
          [{ text: 'OK' }],
        );
      }
    } finally {
      setIsCapturing(false);
    }
  };

  const removeScreenshot = () => setScreenshot(null);

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);
    try {
      const contextData = getContextData();
      const mergedData = { ...data, ...contextData };
      if (isAuthenticated && user) {
        mergedData.user_email = user.email;
        mergedData.user_name = user.full_name || '';
      }
      const cleanData = Object.fromEntries(
        Object.entries(mergedData).filter(
          ([, value]) => value !== '' && value !== null && value !== undefined,
        ),
      );
      const submissionData: FeedbackSubmissionRequest = {
        feedback_type: cleanData.feedback_type as any,
        feedback_title: cleanData.feedback_title as string,
        feedback_content: cleanData.feedback_content as string,
        ...cleanData,
        ...(showScreenshot && screenshot && { screenshot }),
      };
      await api.submitFeedback(submissionData);
      analytics.trackEvent('feedback_submitted', {
        feedback_type: data.feedback_type,
        has_screenshot: showScreenshot ? !!screenshot : false,
        rating: data.rating,
        user_authenticated: isAuthenticated,
      });
      Alert.alert(
        'Feedback submitted',
        'Thanks for the feedback — we appreciate it.',
        [
          {
            text: 'OK',
            onPress: () => {
              reset();
              if (showScreenshot) setScreenshot(null);
              onSubmitSuccess?.();
            },
          },
        ],
      );
    } catch (error: any) {
      let errorMessage = 'Failed to submit feedback. Please try again.';
      if (error?.response?.data?.detail) {
        errorMessage = Array.isArray(error.response.data.detail)
          ? error.response.data.detail
              .map((e: any) => e.msg || e.message || 'Validation error')
              .join(', ')
          : error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      Alert.alert('Submission failed', errorMessage, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showRecommend = useMemo(() => {
    if (rating === undefined || rating === null) return false;
    if (rating < 1 || rating > 5) return false;
    return rating >= 3;
  }, [rating]);

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={styles.scroll}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {/* Type chips */}
      <View style={styles.block}>
        <Text style={styles.label}>Feedback type</Text>
        <Controller
          control={control}
          name="feedback_type"
          rules={{ required: 'Please select a feedback type' }}
          render={({ field: { onChange, value } }) => (
            <View style={styles.chipRow}>
              {FEEDBACK_TYPES.map((t) => {
                const selected = value === t.value;
                return (
                  <Pressable
                    key={t.value}
                    onPress={() => onChange(t.value)}
                    style={[
                      styles.chip,
                      selected && styles.chipActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.chipLabel,
                        selected && styles.chipLabelActive,
                      ]}
                    >
                      {t.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          )}
        />
        {errors.feedback_type ? (
          <Text style={styles.errorText}>{errors.feedback_type.message}</Text>
        ) : null}
      </View>

      {/* Title */}
      <Controller
        control={control}
        name="feedback_title"
        rules={{
          required: 'Please enter a title',
          minLength: { value: 5, message: 'Title must be at least 5 characters' },
          maxLength: { value: 200, message: 'Title must be less than 200 characters' },
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <Input
            label="Title"
            placeholder="Brief summary"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            error={errors.feedback_title?.message}
          />
        )}
      />

      {/* Content */}
      <Controller
        control={control}
        name="feedback_content"
        rules={{
          required: 'Please enter your feedback',
          minLength: { value: 10, message: 'Feedback must be at least 10 characters' },
          maxLength: { value: 5000, message: 'Feedback must be less than 5000 characters' },
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <View style={{ gap: 6 }}>
            <Text style={styles.fieldLabel}>Your feedback</Text>
            <TextInput
              placeholder="Tell us what you think\u2026"
              placeholderTextColor={`${fpColors.outline}99`}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              multiline
              numberOfLines={5}
              style={styles.textArea}
            />
            {errors.feedback_content ? (
              <Text style={styles.errorText}>
                {errors.feedback_content.message}
              </Text>
            ) : null}
          </View>
        )}
      />

      {/* Unauthenticated identity fields */}
      {!isAuthenticated ? (
        <View style={{ gap: 10 }}>
          <Controller
            control={control}
            name="user_email"
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Please enter a valid email address',
              },
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                label="Email (optional)"
                placeholder="you@example.com"
                value={value || ''}
                onChangeText={onChange}
                onBlur={onBlur}
                keyboardType="email-address"
                autoCapitalize="none"
                error={errors.user_email?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="user_name"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                label="Name (optional)"
                placeholder="Your name"
                value={value || ''}
                onChangeText={onChange}
                onBlur={onBlur}
              />
            )}
          />
        </View>
      ) : null}

      {/* Rating */}
      <View style={styles.block}>
        <Text style={styles.label}>Rate your experience</Text>
        <Controller
          control={control}
          name="rating"
          render={({ field: { onChange, value } }) => (
            <StarRating rating={value || 0} onRatingChange={onChange} />
          )}
        />
      </View>

      {/* Recommend */}
      {showRecommend ? (
        <Controller
          control={control}
          name="would_recommend"
          render={({ field: { onChange, value } }) => (
            <View style={styles.block}>
              <Text style={styles.label}>Would you recommend us?</Text>
              <View style={styles.recommendRow}>
                <Pressable
                  onPress={() => onChange(true)}
                  style={[
                    styles.recommendBtn,
                    value === true && styles.recommendBtnActive,
                  ]}
                >
                  <MaterialIcons
                    name="thumb-up"
                    size={16}
                    color={
                      value === true ? fpColors.onPrimary : fpColors.onSurface
                    }
                  />
                  <Text
                    style={[
                      styles.recommendLabel,
                      value === true && styles.recommendLabelActive,
                    ]}
                  >
                    Yes
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => onChange(false)}
                  style={[
                    styles.recommendBtn,
                    value === false && styles.recommendBtnActive,
                  ]}
                >
                  <MaterialIcons
                    name="thumb-down"
                    size={16}
                    color={
                      value === false ? fpColors.onPrimary : fpColors.onSurface
                    }
                  />
                  <Text
                    style={[
                      styles.recommendLabel,
                      value === false && styles.recommendLabelActive,
                    ]}
                  >
                    No
                  </Text>
                </Pressable>
              </View>
            </View>
          )}
        />
      ) : null}

      {/* Screenshot */}
      {showScreenshot ? (
        <View style={styles.block}>
          <Text style={styles.label}>Screenshot (optional)</Text>
          {screenshot ? (
            <View style={styles.screenshotWrap}>
              <Image
                source={{ uri: URL.createObjectURL(screenshot) }}
                style={styles.screenshotImage}
                resizeMode="contain"
              />
              <Pressable
                onPress={removeScreenshot}
                style={styles.screenshotRemove}
                hitSlop={6}
              >
                <MaterialIcons
                  name="close"
                  size={16}
                  color={fpColors.error}
                />
              </Pressable>
            </View>
          ) : (
            <Pressable
              onPress={handleCaptureScreenshot}
              disabled={isCapturing || !isScreenshotSupported()}
              style={({ pressed }) => [
                styles.captureBtn,
                pressed && { opacity: 0.85 },
                (!isScreenshotSupported() || isCapturing) && { opacity: 0.6 },
              ]}
            >
              {isCapturing ? (
                <ActivityIndicator size="small" color={fpColors.primary} />
              ) : (
                <MaterialIcons
                  name="photo-camera"
                  size={18}
                  color={fpColors.primary}
                />
              )}
              <Text style={styles.captureLabel}>
                {isCapturing ? 'Capturing\u2026' : 'Capture screenshot'}
              </Text>
            </Pressable>
          )}
        </View>
      ) : null}

      {/* Follow up */}
      <Controller
        control={control}
        name="follow_up_requested"
        render={({ field: { onChange, value } }) => (
          <Pressable
            onPress={() => onChange(!value)}
            style={styles.checkRow}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: !!value }}
          >
            <View
              style={[
                styles.checkbox,
                value && styles.checkboxActive,
              ]}
            >
              {value ? (
                <MaterialIcons
                  name="check"
                  size={12}
                  color={fpColors.onPrimary}
                />
              ) : null}
            </View>
            <Text style={styles.checkLabel}>
              I\u2019d like a follow-up on this feedback
            </Text>
          </Pressable>
        )}
      />

      {/* Submit row */}
      <View style={styles.submitRow}>
        {showCancelButton ? (
          <Pressable
            onPress={onCancel || (() => {})}
            style={({ pressed }) => [
              styles.cancelBtn,
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text style={styles.cancelLabel}>Cancel</Text>
          </Pressable>
        ) : null}
        <View style={{ flex: showCancelButton ? 2 : 1 }}>
          <PrimaryCTA
            label={isSubmitting ? 'Submitting\u2026' : 'Submit feedback'}
            onPress={handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={isSubmitting}
            iconRight={isSubmitting ? null : 'send'}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: { padding: 4, gap: 14, paddingBottom: 20 },
  block: { gap: 10 },
  label: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  fieldLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  chipActive: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  chipLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  chipLabelActive: { color: fpColors.onPrimary },

  textArea: {
    minHeight: 110,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    padding: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    textAlignVertical: 'top',
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  errorText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.error,
  },

  recommendRow: { flexDirection: 'row', gap: 10 },
  recommendBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  recommendBtnActive: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  recommendLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  recommendLabelActive: { color: fpColors.onPrimary },

  captureBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: `${fpColors.primary}10`,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: `${fpColors.primary}55`,
  },
  captureLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
  screenshotWrap: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  screenshotImage: { width: '100%', height: 180 },
  screenshotRemove: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    justifyContent: 'center',
    alignItems: 'center',
  },

  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 4,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: fpColors.outline,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxActive: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primary,
  },
  checkLabel: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurface,
  },

  submitRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'stretch',
    marginTop: 6,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
});
